import { Doc } from '@dadohr/shared/domainTypes/document';
import { isCustomFieldKey } from '@dadohr/shared/domainTypes/org';
import {
  IUserImportMeta,
  IUserImportMetaOptions,
  UserImportStatus
} from '@dadohr/shared/domainTypes/user';
import styled from '@emotion/styled';
import { Button, Dialog, Typography } from '@material-ui/core';
import { Download, Info, Plus, Upload } from 'lucide-react';
import { ChangeEvent, useMemo, useRef, useState } from 'react';
import shortid from 'shortid';
import { AlertCircleRed } from '../../../../components/AlertCircleRed';
import { ButtonWithPromise } from '../../../../components/ButtonWithPromise';
import { ColumnSelector } from '../../../../components/ColumnSelector';
import { DialogActionsHeader } from '../../../../components/DialogActionsHeader';
import { DialogActionsWithSlots } from '../../../../components/DialogActionsWithSlots';
import { DialogContentWithoutPadding } from '../../../../components/DialogContentWithoutPadding';
import { Loader } from '../../../../components/Loader';
import { NoAccess } from '../../../../components/NoAccess';
import { UserTable } from '../../../../components/UserTable';
import {
  UserTableColumn,
  useUserTableColumnsAndSorters
} from '../../../../components/UserTable/columns';
import { CSVImportIssuesTable } from '../../../../components/UserTable/CSVImportIssuesTable';
import { useExportUsersCsv } from '../../../../components/UserTable/useExportUsersCsv';
import { useDialogState } from '../../../../hooks/useDialogState';
import { usePromise } from '../../../../hooks/usePromise';
import { useUnsafeTranslation } from '../../../../i18next';
import { FlexContainer } from '../../../../layout/Flex';
import { PageBody } from '../../../../layout/PageBody';
import {
  PageToolbar,
  PageToolbarActions,
  PageToolbarTitle
} from '../../../../layout/PageToolbar';
import { useRoutes, useStringQueryParam } from '../../../../routes';
import { useCurrentUser } from '../../../../services/currentUser';
import { downloadFile } from '../../../../services/file';
import { storage } from '../../../../services/firebase';
import { hasPermissionToAccessPage } from '../../../../services/permissions';
import {
  matchUserBySearchTerm,
  toSearchTermRegexp,
  useUsersByOrgId
} from '../../../../services/user';
import {
  downloadCurrentCSV as _downloadCurrentCSV,
  commitImport,
  downloadEmptyCSVImportTemplate,
  getImportDiffSet,
  startCSVImport,
  useCSVImportSource,
  useImportById
} from '../../../../services/user/import';

const ModalContentContainerCentered = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '100%'
}));

const ModalContentContainer = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: p.theme.spacing(4),
  height: '100%'
}));
const ModalContentSmallCentered = styled('div')<{ width?: number }>((p) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: p.theme.spacing(2),
  justifyContent: 'center',
  maxWidth: p.theme.spacing(p.width || 62),
  color: p.theme.custom.colors.gray.w500
}));

const List = styled('ul')`
  color: ${(p) => p.theme.custom.colors.gray.w500};
  padding-left: ${(p) => p.theme.spacing(4)}px;
  margin: 0;
  li {
    margin-top: ${(p) => p.theme.spacing(0.5)}px;
  }
`;

const ButtonContainer = styled('div')((p) => ({
  margin: 'auto'
}));

const ErrorsHintContainer = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: p.theme.spacing(),
  border: p.theme.custom.border.standard,
  borderRadius: p.theme.custom.border.radius(),
  padding: `${p.theme.spacing(1)}px ${p.theme.spacing(1.5)}px`
}));

const ProcessingLoader = () => {
  const { t } = useUnsafeTranslation();
  return (
    <ModalContentContainerCentered>
      <ModalContentSmallCentered>
        <Typography variant="h2" align="center">
          {t('Processing your import...')}
        </Typography>
        <Loader height={50} />
      </ModalContentSmallCentered>
    </ModalContentContainerCentered>
  );
};

const ReviewStepInner = ({
  orgId,
  importMetaDoc,
  step,
  onClose,
  goBack
}: {
  orgId: string;
  importMetaDoc: Doc<IUserImportMeta>;
  step: number;
  onClose: () => void;
  goBack: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const importId = importMetaDoc.id;
  const stats = importMetaDoc.data.stats;
  const [diffSet, loading] = usePromise(async () => {
    return getImportDiffSet(orgId, importId);
  }, [orgId, importId]);

  const totalUpdated = stats.added + stats.changed;
  const noOfIssues = stats.issues;
  const totalReadyToImport = stats.notChanged + totalUpdated;
  const total = totalReadyToImport + noOfIssues;

  console.log('STATS', stats, total, noOfIssues, importMetaDoc.data.status);

  const applyChanges = async () => {
    await commitImport(orgId, importMetaDoc.id);
  };

  console.log(importMetaDoc.data);
  const onlyIssues =
    importMetaDoc.data.stats.issues > 0 &&
    importMetaDoc.data.stats.issues === importMetaDoc.data.stats.total;

  const getInnerContent = () => {
    if (importMetaDoc.data.status === UserImportStatus.ERROR) {
      // TODO - we can look at the actual error encointered and give people
      // a more precise hint about what's wrong with the source
      return (
        <ModalContentContainerCentered>
          <ModalContentSmallCentered>
            <Typography variant="h2">
              {t('Failed to process your CSV')}
            </Typography>
            <Typography align="center">
              {t(
                'Make sure the CSV is formatted correctly, including all dates'
              )}
            </Typography>
          </ModalContentSmallCentered>
        </ModalContentContainerCentered>
      );
    }
    if (stats.notChanged === total) {
      return (
        <ModalContentContainerCentered>
          <ModalContentSmallCentered>
            <Typography variant="h2">{t('No new data to import')}</Typography>
            <Typography align="center">
              {t(
                'Your CSV contains no new employees and no new data for employees already imported into Dado'
              )}
            </Typography>
          </ModalContentSmallCentered>
        </ModalContentContainerCentered>
      );
    }

    if (
      importMetaDoc.data.status === UserImportStatus.DONE &&
      total !== noOfIssues
    ) {
      return (
        <ModalContentContainerCentered>
          <ModalContentSmallCentered width={80}>
            <Typography variant="h2">
              {t('import.csvWizard.successTitle', { count: totalUpdated })}
            </Typography>
            {!!stats.notChanged && (
              <Typography align="center">
                {t('import.csvWizard.successSubTitle', {
                  count: stats.notChanged
                })}
              </Typography>
            )}
          </ModalContentSmallCentered>
        </ModalContentContainerCentered>
      );
    }

    if (!!noOfIssues) {
      if (!diffSet || loading) {
        return <ProcessingLoader />;
      }
      return (
        <ModalContentContainer>
          <ErrorsHintContainer>
            <FlexContainer>
              <AlertCircleRed size={20} />
              <div>
                {t('import.csvWizard.errorsDetected', { count: noOfIssues })}
              </div>
            </FlexContainer>
            <Button
              size="small"
              variant="outlined"
              onClick={() => goBack()}
              startIcon={<Upload size={16} />}
            >
              {t('Replace CSV')}
            </Button>
          </ErrorsHintContainer>
          <CSVImportIssuesTable ds={diffSet.issues} />
        </ModalContentContainer>
      );
    }
  };
  return (
    <>
      <DialogContentWithoutPadding>
        {getInnerContent()}
      </DialogContentWithoutPadding>

      <DialogActionsWithSlots
        left={<Typography variant="caption">{`Step ${step}/3`}</Typography>}
        right={
          <FlexContainer>
            {importMetaDoc.data.status === UserImportStatus.PREVIEW &&
              importMetaDoc.data.stats.issues > 0 &&
              !onlyIssues && (
                <>
                  <Typography variant="caption">
                    <FlexContainer spacing={0.5}>
                      <Info size={16} />
                      {t(' Only employees without errors will be imported')}
                    </FlexContainer>
                  </Typography>

                  {importMetaDoc.data.stats.issues ===
                  importMetaDoc.data.stats.total ? (
                    <Button></Button>
                  ) : (
                    <ButtonWithPromise
                      color="primary"
                      variant="contained"
                      disabled={loading}
                      onClick={async () => {
                        await applyChanges();
                      }}
                      pending={`${t('Importing')}...`}
                    >
                      {t('Import')}{' '}
                    </ButtonWithPromise>
                  )}
                </>
              )}
            {importMetaDoc.data.status === UserImportStatus.PREVIEW &&
              !importMetaDoc.data.stats.issues && (
                <Button disabled variant="contained" color="primary">
                  {t('Import')}
                </Button>
              )}
            {(importMetaDoc.data.status === UserImportStatus.DONE ||
              importMetaDoc.data.status === UserImportStatus.ERROR ||
              onlyIssues) && (
              <Button color="primary" variant="contained" onClick={onClose}>
                {t('Close')}
              </Button>
            )}
          </FlexContainer>
        }
      />
    </>
  );
};

const ReviewStep = ({
  orgId,
  importMetaDocId,
  step,
  onClose,
  goBack
}: {
  orgId: string;
  importMetaDocId: string;
  step: number;
  onClose: () => void;
  goBack: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const [importMetaDoc, loading] = useImportById(importMetaDocId);

  if (
    loading ||
    !importMetaDoc ||
    (importMetaDoc && importMetaDoc.data.status === UserImportStatus.PENDING)
  ) {
    return (
      <>
        <DialogContentWithoutPadding>
          <ProcessingLoader />
        </DialogContentWithoutPadding>

        <DialogActionsWithSlots
          left={<Typography variant="caption">{`Step ${step}/3`}</Typography>}
          right={
            <Button disabled color="primary" variant="contained">
              {t('Import')}
            </Button>
          }
        />
      </>
    );
  }

  return (
    <ReviewStepInner
      orgId={orgId}
      importMetaDoc={importMetaDoc}
      step={step}
      onClose={onClose}
      goBack={goBack}
    />
  );
};

const CSVImportModal = ({
  open,
  onClose,
  employeeCount
}: {
  open: boolean;
  onClose: () => void;
  employeeCount: number;
}) => {
  const { t } = useUnsafeTranslation();
  type Step = 1 | 2 | 3;
  const [state, setState] = useState<{
    importMetaDocId: string;
    uploading: boolean;
    step: Step;
  }>({
    step: 1,
    importMetaDocId: '',
    uploading: false
  });

  const setStep = (nextStep: Step) =>
    setState((p) => ({ ...p, step: nextStep }));
  const setUploading = (nextUploading: boolean) =>
    setState((p) => ({ ...p, uploading: nextUploading }));

  const { step, importMetaDocId, uploading } = state;

  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const currentUser = useCurrentUser();
  const orgId = currentUser.org.id;
  const [uploadError, setUploadError] = useState<string>();

  const closeDialog = () => {
    setStep(1);
    onClose();
  };

  const startImport = async (storageRef: string) => {
    console.log('STARTING IMPORT');
    const options: IUserImportMetaOptions = {
      ignoreMissing: true,
      deactivateInsteadOfRemove: true,
      autoEnroll: false,
      useCSVStrictValidation: true,
      autoApply: false,
      alwaysPreviewIssues: true,
      autoApplyOnNoIssues: true
    };
    const response = await startCSVImport(orgId, options, storageRef);
    return response;
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      setUploading(true);
      try {
        const filename = `${shortid()}.csv`;
        const filePath = `/csvImports/${orgId}/${filename}`;
        const ref = storage().ref(filePath);
        await ref.put(selectedFile);

        const result = await startImport(filename);

        if (result.status === 'OK') {
          const nextImportId = result.importDocId;
          setState((p) => ({ ...p, step: 3, importMetaDocId: nextImportId }));
          setUploadError(undefined);
          return;
        } else {
          console.error('Update csv import source error', result);
          if (result.errorMessage === 'INVALID FILE TYPE') {
            setUploadError(t('Please upload a valid CSV file'));
          }
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        setUploadError(
          t(
            'There was an error while uploading the file please try again later'
          )
        );
      }
    }

    setUploading(false);
  };

  const downloadCurrentCsv = async () => {
    const csvString = await _downloadCurrentCSV(orgId);
    downloadFile('Dado - Employee List', 'csv', csvString);
  };

  const selectFile = () => {
    if (hiddenFileInput.current) {
      hiddenFileInput.current.click();
    }
  };

  // if (importMetaDoc?.data.status === UserImportStatus.DONE) {
  //   setChangesApplied(true);
  // }

  const getStepContent = () => {
    switch (step) {
      case 1:
        return (
          <>
            <DialogContentWithoutPadding>
              <ModalContentContainerCentered>
                <ModalContentSmallCentered>
                  {employeeCount === 0 ? (
                    <>
                      <Typography variant="h2" align="center">
                        {t('No employees')}
                      </Typography>
                      <Typography component="p" align="center">
                        {t(
                          ' Download the CSV template to get started. Template includes mandatory attributes and the option to add your own attributes as well.'
                        )}
                      </Typography>
                      <Button
                        variant="outlined"
                        onClick={() => downloadEmptyCSVImportTemplate()}
                      >
                        {t('Download CSV template')}
                      </Button>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => setStep(2)}
                        size="small"
                      >
                        {t('I already have the template ready to upload')}
                      </Button>
                    </>
                  ) : (
                    <>
                      <Typography variant="h5">
                        {t('Upload a new CSV to')}:
                      </Typography>
                      <List>
                        <li>{t('Update existing employee data')}</li>
                        <li>{t('Add employees')}</li>
                        <li>{t('Add employee attributes')}</li>
                      </List>

                      <ButtonWithPromise
                        variant="text"
                        color="primary"
                        pending={t('Downloading...')}
                        onClick={() => downloadCurrentCsv()}
                      >
                        {t('Download CSV with current employee data')}
                      </ButtonWithPromise>
                    </>
                  )}
                </ModalContentSmallCentered>
              </ModalContentContainerCentered>
            </DialogContentWithoutPadding>

            <DialogActionsWithSlots
              left={
                <Typography variant="caption">{`Step ${step}/3`}</Typography>
              }
              right={
                <FlexContainer>
                  <Button color="primary" onClick={closeDialog}>
                    {t('Cancel')}
                  </Button>

                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => setStep(2)}
                  >
                    {t('Next')}
                  </Button>
                </FlexContainer>
              }
            />
          </>
        );
      case 2:
        return (
          <>
            <DialogContentWithoutPadding>
              <ModalContentContainerCentered>
                <ModalContentSmallCentered>
                  {uploading ? (
                    <>
                      <Typography variant="h2" align="center">
                        {t('Uploading...')}
                      </Typography>
                      <Loader height={50} />
                    </>
                  ) : (
                    <>
                      <Typography variant="h2" align="center">
                        {t('Upload employee data file')}
                      </Typography>

                      <Typography align="left">
                        {t('The only mandatory fields are')}:
                        <List>
                          <li>{t('Work email')}</li>
                          <li>{t('First name')}</li>
                          <li>{t('Last name')}</li>
                        </List>
                      </Typography>

                      <ButtonContainer>
                        <Button
                          size="small"
                          disabled={uploading}
                          startIcon={<Upload size={16} />}
                          variant="outlined"
                          onClick={selectFile}
                        >
                          {t('Upload CSV')}
                          <input
                            type="file"
                            accept="text/csv"
                            hidden
                            onChange={handleFileChange}
                            ref={hiddenFileInput}
                          />
                        </Button>
                      </ButtonContainer>
                      {uploadError && (
                        <Typography align="center" color="error">
                          {uploadError}
                        </Typography>
                      )}
                    </>
                  )}
                </ModalContentSmallCentered>
              </ModalContentContainerCentered>
            </DialogContentWithoutPadding>

            <DialogActionsWithSlots
              left={
                <Typography variant="caption">{`Step ${step}/3`}</Typography>
              }
              right={
                <FlexContainer>
                  <Button
                    color="primary"
                    onClick={() => {
                      setStep(1);
                    }}
                  >
                    {t('Back')}
                  </Button>
                  <ButtonWithPromise
                    color="primary"
                    variant="contained"
                    disabled={true}
                    pending={`${t('Reviewing')}...`}
                    onClick={async () => {
                      // this can never be clicked - we jump to step 3 automatically
                    }}
                  >
                    {t('Review')}
                  </ButtonWithPromise>
                </FlexContainer>
              }
            />
          </>
        );
      case 3:
        // TODO - this might cause a brief flash and is not ideal
        return importMetaDocId ? (
          <ReviewStep
            orgId={orgId}
            importMetaDocId={importMetaDocId}
            step={3}
            onClose={closeDialog}
            goBack={() =>
              setState((p) => ({
                ...p,
                step: 2,
                importMetaDocId: '',
                uploading: false
              }))
            }
          />
        ) : null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogActionsHeader
        onClose={() => {
          onClose();
        }}
        left={<Typography variant="h4">{t('Import Employee Data')}</Typography>}
      />

      {getStepContent()}
    </Dialog>
  );
};

const NoUsersHintContainer = styled('div')((p) => ({
  height: '50%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',

  gap: p.theme.spacing(2),
  color: p.theme.custom.colors.gray.w500
}));

const NoUsersHint = () => {
  const { t } = useUnsafeTranslation();
  return (
    <NoUsersHintContainer>
      <Typography variant="h2" align="center">
        {t('No employees')}
      </Typography>
      <Typography component="p" align="center">
        {t('Download the CSV template to get started')}
      </Typography>
      <Button
        variant="outlined"
        onClick={() => downloadEmptyCSVImportTemplate()}
      >
        {t('Download CSV template')}
      </Button>
    </NoUsersHintContainer>
  );
};

const ContentOrgUsersOverviewStandalone = () => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const [users, loading] = useUsersByOrgId(org.id);
  const { ROUTES } = useRoutes();
  const [q] = useStringQueryParam('q', '');
  const { columns: allColumns, sorters } = useUserTableColumnsAndSorters(
    org.customUserFields
  );

  const selectedUsers = useMemo(() => {
    if (!users) {
      return null;
    }
    if (!q) {
      return users;
    }
    const regexp = toSearchTermRegexp(q);
    return users.filter((u) => matchUserBySearchTerm(regexp, u));
  }, [q, users]);

  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);

  // const [importSources] = useUserImportDataSources(org.id);
  // const csvImportsource = useMemo(()=>{
  //   if(importSources){
  //     const csvImportSource = importSources.filter(s=> s.data.type === UserImportSourceType.MANUAL_CSV)[0]
  //   }
  //   return undefined;
  // },[importSources])

  const [csvImportSource] = useCSVImportSource(org.id);
  const [selectedColumns, setSelectedColumns] = useState<UserTableColumn[]>([]);

  const { exportCsv, canExport } = useExportUsersCsv({
    users,
    selectedUsers,
    selectedColumns,
    customUserFields: org.customUserFields,
    dateFormat: org.dateFormats.MMDDYYYY
  });

  const initialized = useRef(false);
  const columns = useMemo(() => {
    const columnsFromMappings = allColumns.filter((c) =>
      csvImportSource?.data.customMappings.map((m) => m.dadoKey).includes(c.key)
    );

    if (allColumns && csvImportSource && !initialized.current) {
      initialized.current = true;
      setSelectedColumns(
        columnsFromMappings.filter(
          (c) =>
            !isCustomFieldKey(c.key) &&
            c.key !== 'address' &&
            c.key !== 'terminationDate' &&
            c.key !== 'dateOfBirth' &&
            c.key !== 'nextBirthday'
        )
      );
    }
    return columnsFromMappings;
  }, [allColumns, csvImportSource]);

  return (
    <PageBody noTopPadding>
      <PageToolbar sticky>
        <PageToolbarTitle>
          <Typography component="h1" variant="h1">
            {t('Employees')}
          </Typography>
        </PageToolbarTitle>
        <PageToolbarActions>
          <ColumnSelector
            value={selectedColumns}
            onChange={setSelectedColumns}
            columns={columns}
            requiredColumns={new Set(['name'])}
            withAllOption
            withSearch
          />
          {canExport && (
            <ButtonWithPromise
              startIcon={<Download size={16} />}
              variant="outlined"
              pending={`${t('Exporting')}...`}
              onClick={exportCsv}
            >
              {t('Export')}
            </ButtonWithPromise>
          )}
          <Button
            startIcon={<Plus size={16} />}
            variant="contained"
            color="primary"
            onClick={openDialog}
          >
            {t('Import employee data')}
          </Button>
        </PageToolbarActions>
      </PageToolbar>
      {loading && <Loader height={500} />}
      {users && selectedUsers && (
        <>
          {users.length ? (
            <UserTable
              ds={selectedUsers}
              columns={selectedColumns}
              sorters={sorters}
              variant="contained"
              rowToHref={(d) => ROUTES.users.profile.url(d.id)}
              headProps={{ sticky: true, offset: 124 }}
              dateFormat={org.dateFormats.MMDDYYYY}
            />
          ) : (
            <NoUsersHint />
          )}

          {dialogOpen && (
            <CSVImportModal
              open={dialogOpen}
              onClose={closeDialog}
              employeeCount={users.length}
            />
          )}
        </>
      )}
    </PageBody>
  );
};

export const PageOrgUsersStandaloneOverview = () => {
  const currentUser = useCurrentUser();
  const { ROUTES } = useRoutes();

  if (!hasPermissionToAccessPage(currentUser, ROUTES.users.overview)) {
    return <NoAccess />;
  }

  return <ContentOrgUsersOverviewStandalone />;
};
